import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Archive, BriefcaseBusiness, Building2, CalendarCheck, Globe, ListChecks, Mail, Phone, RefreshCw, Search, UserRound } from "lucide-react";
import {
    getCrmActivities,
    getCrmCompanies,
    getCrmContacts,
    getCrmDeals,
    getCrmLeads,
    getCrmTasks,
} from "../services/api";

const companyStatusLabels = {
    active: "Активна",
    paused: "На паузе",
    archived: "Архив",
};

const leadStatusLabels = {
    new: "Новый",
    contacted: "Связались",
    qualified: "Квалифицирован",
    converted: "Конвертирован",
    lost: "Потерян",
};

const dealStageLabels = {
    new: "Новая",
    qualified: "Квалификация",
    proposal: "Предложение",
    won: "Выиграна",
    lost: "Потеряна",
};

const taskStatusLabels = {
    open: "Открыта",
    in_progress: "В работе",
    blocked: "Блокер",
    done: "Готово",
};

const activityTypeLabels = {
    note: "Заметка",
    call: "Звонок",
    meeting: "Встреча",
    task: "Задача",
};

const formatMoney = (value) => {
    const amount = Number(value || 0);
    return amount ? `${amount.toLocaleString("ru-RU")} ₽` : "0 ₽";
};

const formatDateTime = (value) => (value ? new Date(value).toLocaleString("ru-RU") : "Без даты");

const fullContactName = (contact = {}) => (
    [contact.first_name, contact.last_name].filter(Boolean).join(" ") || "Контакт без имени"
);

const normalizeSearch = (value) => String(value || "").trim().toLowerCase();

const resolveLeadCompanyId = (lead, companiesById, companyIdByName) => {
    if (lead.converted_company_id && companiesById.has(Number(lead.converted_company_id))) {
        return Number(lead.converted_company_id);
    }
    const namedCompanyId = companyIdByName.get(normalizeSearch(lead.company_name));
    return namedCompanyId || null;
};

const buildCounterpartyRows = ({ companies, contacts, leads, activities, deals, tasks }) => {
    const companiesById = new Map(companies.map((company) => [company.id, company]));
    const companyIdByName = new Map(
        companies
            .filter((company) => company.name)
            .map((company) => [normalizeSearch(company.name), company.id])
    );
    const contactsById = new Map(contacts.map((contact) => [contact.id, contact]));
    const dealsById = new Map(deals.map((deal) => [deal.id, deal]));
    const leadCompanyById = new Map();

    leads.forEach((lead) => {
        const companyId = resolveLeadCompanyId(lead, companiesById, companyIdByName);
        if (companyId) {
            leadCompanyById.set(lead.id, companyId);
        }
    });

    const rows = companies.map((company) => ({
        key: `company-${company.id}`,
        company,
        contacts: [],
        leads: [],
        activities: [],
        deals: [],
        tasks: [],
        isUnlinked: false,
    }));
    const rowByCompanyId = new Map(rows.map((row) => [row.company.id, row]));
    const unlinkedRow = {
        key: "unlinked",
        company: {
            id: null,
            name: "Без привязки к компании",
            status: "active",
            owner_username: "",
        },
        contacts: [],
        leads: [],
        activities: [],
        deals: [],
        tasks: [],
        isUnlinked: true,
    };

    const rowForCompanyId = (companyId) => rowByCompanyId.get(Number(companyId)) || unlinkedRow;
    const rowForContactId = (contactId) => {
        const contact = contactsById.get(Number(contactId));
        return contact?.company_id ? rowForCompanyId(contact.company_id) : unlinkedRow;
    };
    const rowForDealId = (dealId) => {
        const deal = dealsById.get(Number(dealId));
        if (!deal) {
            return unlinkedRow;
        }
        if (deal.company_id) {
            return rowForCompanyId(deal.company_id);
        }
        if (deal.contact_id) {
            return rowForContactId(deal.contact_id);
        }
        return unlinkedRow;
    };
    const rowForLeadId = (leadId) => {
        const companyId = leadCompanyById.get(Number(leadId));
        return companyId ? rowForCompanyId(companyId) : unlinkedRow;
    };

    contacts.forEach((contact) => {
        (contact.company_id ? rowForCompanyId(contact.company_id) : unlinkedRow).contacts.push(contact);
    });
    leads.forEach((lead) => {
        const companyId = leadCompanyById.get(lead.id);
        (companyId ? rowForCompanyId(companyId) : unlinkedRow).leads.push(lead);
    });
    activities.forEach((activity) => {
        if (activity.company_id) {
            rowForCompanyId(activity.company_id).activities.push(activity);
        } else if (activity.contact_id) {
            rowForContactId(activity.contact_id).activities.push(activity);
        } else {
            unlinkedRow.activities.push(activity);
        }
    });
    deals.forEach((deal) => {
        if (deal.company_id) {
            rowForCompanyId(deal.company_id).deals.push(deal);
        } else if (deal.contact_id) {
            rowForContactId(deal.contact_id).deals.push(deal);
        } else {
            unlinkedRow.deals.push(deal);
        }
    });
    tasks.forEach((task) => {
        if (task.company_id) {
            rowForCompanyId(task.company_id).tasks.push(task);
        } else if (task.contact_id) {
            rowForContactId(task.contact_id).tasks.push(task);
        } else if (task.deal_id) {
            rowForDealId(task.deal_id).tasks.push(task);
        } else if (task.lead_id) {
            rowForLeadId(task.lead_id).tasks.push(task);
        } else {
            unlinkedRow.tasks.push(task);
        }
    });

    const hasUnlinkedData = ["contacts", "leads", "activities", "deals", "tasks"].some((key) => unlinkedRow[key].length);
    return hasUnlinkedData ? [...rows, unlinkedRow] : rows;
};

const CounterpartyMiniList = ({ emptyText, items, renderItem }) => (
    <div className="counterparty-mini-list">
        {items.length ? items.slice(0, 4).map(renderItem) : <span className="counterparty-empty">{emptyText}</span>}
        {items.length > 4 && <span className="counterparty-more">+{items.length - 4} ещё</span>}
    </div>
);

const CounterpartiesDirectory = () => {
    const [companies, setCompanies] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [leads, setLeads] = useState([]);
    const [activities, setActivities] = useState([]);
    const [deals, setDeals] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [query, setQuery] = useState("");
    const [includeArchived, setIncludeArchived] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [message, setMessage] = useState("");

    const loadDirectory = useCallback(async () => {
        setIsLoading(true);
        setMessage("");
        try {
            const params = includeArchived ? { include_archived: true } : {};
            const [
                companiesResponse,
                contactsResponse,
                leadsResponse,
                activitiesResponse,
                dealsResponse,
                tasksResponse,
            ] = await Promise.all([
                getCrmCompanies(params),
                getCrmContacts(params),
                getCrmLeads(params),
                getCrmActivities(params),
                getCrmDeals(params),
                getCrmTasks(params),
            ]);
            setCompanies(companiesResponse.data || []);
            setContacts(contactsResponse.data || []);
            setLeads(leadsResponse.data || []);
            setActivities(activitiesResponse.data || []);
            setDeals(dealsResponse.data || []);
            setTasks(tasksResponse.data || []);
        } catch (err) {
            setMessage("Не удалось загрузить справочник контрагентов.");
        } finally {
            setIsLoading(false);
        }
    }, [includeArchived]);

    useEffect(() => {
        loadDirectory();
    }, [loadDirectory]);

    const rows = useMemo(() => buildCounterpartyRows({
        companies,
        contacts,
        leads,
        activities,
        deals,
        tasks,
    }), [activities, companies, contacts, deals, leads, tasks]);

    const filteredRows = useMemo(() => {
        const search = normalizeSearch(query);
        if (!search) {
            return rows;
        }
        return rows.filter((row) => {
            const values = [
                row.company.name,
                row.company.legal_name,
                row.company.phone,
                row.company.email,
                row.company.website,
                ...row.contacts.flatMap((contact) => [fullContactName(contact), contact.email, contact.phone, contact.position]),
                ...row.leads.flatMap((lead) => [lead.title, lead.company_name, lead.contact_name, lead.email, lead.phone, lead.source]),
                ...row.activities.flatMap((activity) => [activity.title, activity.body, activity.type]),
                ...row.deals.flatMap((deal) => [deal.title, deal.stage, deal.description]),
                ...row.tasks.flatMap((task) => [task.title, task.body, task.status, task.assigned_to_username]),
            ];
            return values.some((value) => normalizeSearch(value).includes(search));
        });
    }, [query, rows]);

    const totals = useMemo(() => ({
        companies: companies.length,
        contacts: contacts.length,
        leads: leads.length,
        activities: activities.length,
        deals: deals.length,
        tasks: tasks.length,
        activeAmount: deals
            .filter((deal) => !["won", "lost"].includes(deal.stage))
            .reduce((sum, deal) => sum + Number(deal.amount || 0), 0),
    }), [activities, companies, contacts, deals, leads, tasks]);

    return (
        <main className="counterparties-page">
            <section className="counterparties-head">
                <div className="counterparties-head-text">
                    <span>CRM · Справочник</span>
                    <h1>Контрагенты</h1>
                    <p>Компании со всеми связанными контактами, лидами, активностями, сделками и задачами — в одной карточке.</p>
                </div>
                <button className="counterparties-refresh" type="button" onClick={loadDirectory} disabled={isLoading}>
                    <RefreshCw size={15} /> Обновить
                </button>
            </section>

            <section className="counterparties-toolbar">
                <label className="counterparties-search">
                    <Search size={16} />
                    <input
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder="Поиск по компании, контакту, лиду, сделке или задаче"
                    />
                </label>
                <label className="counterparties-archive-toggle">
                    <input
                        type="checkbox"
                        checked={includeArchived}
                        onChange={(event) => setIncludeArchived(event.target.checked)}
                    />
                    <span>Показать архив</span>
                </label>
            </section>

            {message && <p className="form-message">{message}</p>}

            <section className="counterparties-summary" aria-label="Сводка по справочнику">
                <article>
                    <span className="counterparties-summary-icon"><Building2 size={15} /></span>
                    <div><strong>{totals.companies}</strong><span>Компании</span></div>
                </article>
                <article>
                    <span className="counterparties-summary-icon"><UserRound size={15} /></span>
                    <div><strong>{totals.contacts}</strong><span>Контакты</span></div>
                </article>
                <article>
                    <span className="counterparties-summary-icon"><BriefcaseBusiness size={15} /></span>
                    <div><strong>{totals.deals}</strong><span>Сделки</span></div>
                </article>
                <article>
                    <span className="counterparties-summary-icon"><ListChecks size={15} /></span>
                    <div><strong>{totals.tasks}</strong><span>CRM-задачи</span></div>
                </article>
                <article className="is-accent">
                    <span className="counterparties-summary-icon"><CalendarCheck size={15} /></span>
                    <div><strong>{formatMoney(totals.activeAmount)}</strong><span>Активная сумма</span></div>
                </article>
            </section>

            <section className="counterparty-directory">
                {isLoading ? (
                    <div className="counterparty-loading">Загружаем справочник...</div>
                ) : filteredRows.length ? (
                    filteredRows.map((row) => (
                        <article className={`counterparty-card ${row.isUnlinked ? "is-unlinked" : ""}`} key={row.key}>
                            <div className="counterparty-card-head">
                                <div className="counterparty-card-heading">
                                    <span className="counterparty-card-icon">
                                        {row.isUnlinked ? <Archive size={19} /> : <Building2 size={19} />}
                                    </span>
                                    <div className="counterparty-card-heading-text">
                                        <div className="counterparty-card-title-row">
                                            <h2>{row.company.name}</h2>
                                            {!row.isUnlinked && row.company.status && (
                                                <span className={`counterparty-status counterparty-status-${row.company.status}`}>
                                                    {companyStatusLabels[row.company.status] || row.company.status}
                                                </span>
                                            )}
                                        </div>
                                        {row.company.legal_name && <p>{row.company.legal_name}</p>}
                                    </div>
                                </div>
                                {!row.isUnlinked && (
                                    <div className="counterparty-facts">
                                        {row.company.owner_username && <span><UserRound size={12} /> Ответственный: {row.company.owner_username}</span>}
                                        {row.company.phone && <span><Phone size={12} /> {row.company.phone}</span>}
                                        {row.company.email && <span><Mail size={12} /> {row.company.email}</span>}
                                        {row.company.website && (
                                            <a href={row.company.website} target="_blank" rel="noreferrer"><Globe size={12} /> {row.company.website}</a>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="counterparty-card-cols">
                                <div className="counterparty-col">
                                    <div className="counterparty-col-head">Контакты <span>{row.contacts.length || ""}</span></div>
                                    <CounterpartyMiniList
                                        emptyText="Нет контактов"
                                        items={row.contacts}
                                        renderItem={(contact) => (
                                            <div className="counterparty-mini-item" key={contact.id}>
                                                <strong>{fullContactName(contact)}</strong>
                                                <small>{[contact.position, contact.phone, contact.email].filter(Boolean).join(" · ") || "Без контактов"}</small>
                                            </div>
                                        )}
                                    />
                                </div>

                                <div className="counterparty-col">
                                    <div className="counterparty-col-head">Лиды <span>{row.leads.length || ""}</span></div>
                                    <CounterpartyMiniList
                                        emptyText="Нет лидов"
                                        items={row.leads}
                                        renderItem={(lead) => (
                                            <div className="counterparty-mini-item" key={lead.id}>
                                                <strong>{lead.title}</strong>
                                                <small>{[leadStatusLabels[lead.status] || lead.status, lead.source, lead.estimated_amount && formatMoney(lead.estimated_amount)].filter(Boolean).join(" · ")}</small>
                                            </div>
                                        )}
                                    />
                                </div>

                                <div className="counterparty-col">
                                    <div className="counterparty-col-head">Активность <span>{row.activities.length || ""}</span></div>
                                    <CounterpartyMiniList
                                        emptyText="Нет активности"
                                        items={row.activities}
                                        renderItem={(activity) => (
                                            <div className="counterparty-mini-item" key={activity.id}>
                                                <strong>{activity.title}</strong>
                                                <small>{[activityTypeLabels[activity.type] || activity.type, formatDateTime(activity.created_at)].filter(Boolean).join(" · ")}</small>
                                            </div>
                                        )}
                                    />
                                </div>

                                <div className="counterparty-col">
                                    <div className="counterparty-col-head">Сделки <span>{row.deals.length || ""}</span></div>
                                    <CounterpartyMiniList
                                        emptyText="Нет сделок"
                                        items={row.deals}
                                        renderItem={(deal) => (
                                            <div className="counterparty-mini-item" key={deal.id}>
                                                <strong>{deal.title}</strong>
                                                <small>{[dealStageLabels[deal.stage] || deal.stage, formatMoney(deal.amount), `${deal.probability || 0}%`].filter(Boolean).join(" · ")}</small>
                                            </div>
                                        )}
                                    />
                                </div>

                                <div className="counterparty-col">
                                    <div className="counterparty-col-head">Задачи <span>{row.tasks.length || ""}</span></div>
                                    <CounterpartyMiniList
                                        emptyText="Нет задач"
                                        items={row.tasks}
                                        renderItem={(task) => (
                                            <div className="counterparty-mini-item" key={task.id}>
                                                <strong>{task.title}</strong>
                                                <small>{[taskStatusLabels[task.status] || task.status, task.assigned_to_username, task.due_at && formatDateTime(task.due_at)].filter(Boolean).join(" · ")}</small>
                                            </div>
                                        )}
                                    />
                                </div>
                            </div>
                        </article>
                    ))
                ) : (
                    <div className="counterparty-loading">Ничего не найдено.</div>
                )}
            </section>
        </main>
    );
};

export default CounterpartiesDirectory;
